import { useSkills } from "@app/lib/swr/skill_configurations";
import type { SkillWithoutInstructionsAndToolsType } from "@app/types/assistant/skill_configuration";
import type { LightWorkspaceType } from "@app/types/user";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

interface SkillsContextType {
  skills: SkillWithoutInstructionsAndToolsType[];
  isSkillsLoading: boolean;
  isSkillsError: boolean;
}

const SkillsContext = createContext<SkillsContextType | undefined>(undefined);

export const useSkillsContext = () => {
  const context = useContext(SkillsContext);
  if (!context) {
    throw new Error("useSkillsContext must be used within a SkillsProvider");
  }
  return context;
};

interface SkillsProviderProps {
  owner: LightWorkspaceType;
  children: ReactNode;
}

export const SkillsProvider = ({ owner, children }: SkillsProviderProps) => {
  const { skills, isSkillsLoading, isSkillsError } = useSkills({
    owner,
    status: "active",
  });

  const value: SkillsContextType = useMemo(() => {
    return {
      // Memoair-managed non-default skills are displayed first: these are the baseline Discover capabilities.
      // Default skills are the ones that are discoverable.
      skills: skills.toSorted((a, b) => {
        const aIsDiscover =
          a.editedBy === null && a.availability !== "users_and_agents";
        const bIsDiscover =
          b.editedBy === null && b.availability !== "users_and_agents";
        if (aIsDiscover !== bIsDiscover) {
          return aIsDiscover ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      }),
      isSkillsLoading,
      isSkillsError,
    };
  }, [skills, isSkillsLoading, isSkillsError]);

  return (
    <SkillsContext.Provider value={value}>{children}</SkillsContext.Provider>
  );
};

SkillsProvider.displayName = "SkillsProvider";
